import {
    IAgentReport,
    IAgentState,
    IRbacTeam,
    IKeyValuePair,
} from '@dxp/core/models';

export const asDate = (
    d: Date | string | undefined | null,
): Date | undefined => {
    if (d == null) return undefined;
    return d instanceof Date ? d : new Date(d);
};

export const areDatesEqual = (a?: Date, b?: Date): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.getTime() === b.getTime();
};

export const areNumberRecordsEqual = (
    a: Record<string, number>,
    b: Record<string, number>,
): boolean => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        if (a[key] !== b[key]) {
            return false;
        }
    }

    return true;
};

export const areIKeyValuePairsEqual = <T>(
    a: IKeyValuePair<T>[],
    b: IKeyValuePair<T>[],
    valueComparer: (x: T, y: T) => boolean = (x, y) => x === y,
): boolean => {
    if (a.length !== b.length) return false;

    const mapA = new Map(a.map(i => [i.key, i.value]));
    const mapB = new Map(b.map(i => [i.key, i.value]));

    if (mapA.size !== mapB.size) return false;

    for (const [key, valueA] of mapA) {
        const valueB = mapB.get(key);
        if (valueB === undefined) return false;
        if (!valueComparer(valueA, valueB)) return false;
    }

    return true;
};

export const areAgentReportsEqual = (
    a: IAgentReport,
    b: IAgentReport,
): boolean => {
    if (a === b) return true;

    return (
        a.id === b.id &&
        a.name === b.name &&
        a.tenantId === b.tenantId &&
        areNumberRecordsEqual(
            a.externalTaskCountPerBusinessUnit,
            b.externalTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgExternalTaskTimeInSecondsPerBusinessUnit,
            b.avgExternalTaskTimeInSecondsPerBusinessUnit,
        ) &&
        areIKeyValuePairsEqual(
            a.workItemKpisPerDispositionCode,
            b.workItemKpisPerDispositionCode,
            (x, y) => x.handledCount === y.handledCount,
        ) &&
        areIKeyValuePairsEqual(
            a.secondsSpentPerState,
            b.secondsSpentPerState,
        ) &&
        areIKeyValuePairsEqual(
            a.secondsSpentPerBreakName,
            b.secondsSpentPerBreakName,
        ) &&
        areDatesEqual(a.loggedInAt, b.loggedInAt) &&
        areDatesEqual(a.loggedOutAt, b.loggedOutAt) &&
        areNumberRecordsEqual(
            a.handledInboundVoiceTaskCountPerBusinessUnit,
            b.handledInboundVoiceTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.handledInboundChatTaskCountPerBusinessUnit,
            b.handledInboundChatTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.handledInboundEmailTaskCountPerBusinessUnit,
            b.handledInboundEmailTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgInboundVoiceHandlingTimeInSecondsPerBusinessUnit,
            b.avgInboundVoiceHandlingTimeInSecondsPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgInboundChatHandlingTimeInSecondsPerBusinessUnit,
            b.avgInboundChatHandlingTimeInSecondsPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgInboundEmailHandlingTimeInSecondsPerBusinessUnit,
            b.avgInboundEmailHandlingTimeInSecondsPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.missedInboundVoiceTaskCountPerBusinessUnit,
            b.missedInboundVoiceTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.missedInboundEmailTaskCountPerBusinessUnit,
            b.missedInboundEmailTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.missedInboundChatTaskCountPerBusinessUnit,
            b.missedInboundChatTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.rejectedInboundVoiceTaskCountPerBusinessUnit,
            b.rejectedInboundVoiceTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.rejectedInboundEmailTaskCountPerBusinessUnit,
            b.rejectedInboundEmailTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.rejectedInboundChatTaskCountPerBusinessUnit,
            b.rejectedInboundChatTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.handledOutboundVoiceTaskCountPerBusinessUnit,
            b.handledOutboundVoiceTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.handledOutboundChatTaskCountPerBusinessUnit,
            b.handledOutboundChatTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.handledOutboundEmailTaskCountPerBusinessUnit,
            b.handledOutboundEmailTaskCountPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgOutboundVoiceHandlingTimeInSecondsPerBusinessUnit,
            b.avgOutboundVoiceHandlingTimeInSecondsPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgOutboundChatHandlingTimeInSecondsPerBusinessUnit,
            b.avgOutboundChatHandlingTimeInSecondsPerBusinessUnit,
        ) &&
        areNumberRecordsEqual(
            a.avgOutboundEmailHandlingTimeInSecondsPerBusinessUnit,
            b.avgOutboundEmailHandlingTimeInSecondsPerBusinessUnit,
        )
    );
};

export const areStringArraysEqualUnordered = (
    a: string[],
    b: string[],
): boolean => {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    const setA = new Set(a);
    if (setA.size !== b.length) return false;

    for (const value of b) {
        if (!setA.has(value)) {
            return false;
        }
    }

    return true;
};

export const areAgentStatesEqual = (
    a: IAgentState,
    b: IAgentState,
): boolean => {
    if (a === b) return true;

    return (
        a.agentId === b.agentId &&
        a.agentName === b.agentName &&
        a.state === b.state &&
        areDatesEqual(a.stateSince, b.stateSince) &&
        a.breakName === b.breakName &&
        areStringArraysEqualUnordered(a.businessUnitIds, b.businessUnitIds) &&
        areDatesEqual(a.breakExpiresAt, b.breakExpiresAt) &&
        a.pendingState === b.pendingState &&
        a.pendingBreakName === b.pendingBreakName
    );
};

export const deriveAgentIdsFromTeams = (
    teams: IRbacTeam[] | null | undefined,
): string[] => {
    if (!teams?.length) return [];
    return Array.from(new Set(teams.flatMap(t => t.memberIds))).sort();
};
